import TextInput from "@shared/v2/TextInput";
import Typography from "@shared/v2/Typography";
import OktaPasswordReset from "../OktaPasswordReset";
import TextLink from "@shared/v2/TextLink";
import {
  useEmailPlaceholders,
  useGmailUnsync,
  useGoogleAuthUrl,
  useGoogleProfile,
  usePersonalErrors,
  usePersonalLoading,
  useUser,
  useValidRoutingNumberOptions,
} from "../reducers/personalReducer/selectors";
import Dropdown from "@shared/v2/Dropdown";
import { useState } from "react";
import FieldLabel from "@shared/v2/FieldLabel";
import Alert from "@shared/v2/Alert";
import {
  CircleInfoSolidV6,
  CircleSmallSolidV6,
  LockKeyholeOpenSolidV6,
  UserGroupSolidV6,
} from "@shared/v2/Icomoon";
import RadioButton from "@shared/v2/RadioButton";
import TextButton from "@shared/v2/TextButton";
import BrivityTextEditor from "@shared/BrivityTextEditor";
import Checkbox from "@shared/v2/Checkbox";
import EnableTextForwarding from "../EnableTextForwarding";
import Button from "@shared/v2/Button";
import { useFeatureFlags } from "../reducers/layoutReducer/selectors";
import Tooltip from "@shared/v2/Tooltip";
import ImageSizeErrorModal from "./ImageSizeErrorModal";
import GmailPermissionModal from "./GmailPermissionModal";
import { useDispatch } from "react-redux";
import { addMls, updatePersonalAccount } from "../reducers/personalReducer/personalReducer";
import { set } from "lodash";
import { Option, User } from "../reducers/personalReducer/types";
import MlsRow from "./MlsRow";
import DisconnectGmailModal from "./DisconnectGmailModal";

// Mirrors UserEmailSyncSetting::SYNC_SETTINGS.
const SYNC_SETTING_OPTIONS = [
  {
    label: "My contacts only",
    value: "primary_agent",
    Icon: UserGroupSolidV6,
    helperText: "Syncs to contacts you're assigned to. Visible to teammates also assigned to that contact.",
  },
  {
    label: "Any matching contact",
    value: "any_matching_contact",
    Icon: LockKeyholeOpenSolidV6,
    helperText:
      "Stay in sync even when coverage shifts. Syncs to any matching contact, even ones you're not assigned to — visible to anyone assigned to that contact.",
  },
];

const Header = ({ action, children }: { action?: React.ReactNode; children: React.ReactNode }) => (
  <div className="tw-flex tw-flex-col tw-gap-[4px]">
    <div className="tw-flex tw-justify-between tw-items-center tw-gap-[16px]">
      {/* @ts-expect-error props */}
      <Typography variant="headline3">{children}</Typography>
      {action}
    </div>
    <hr className="tw-m-0 tw-border-gray-20" />
  </div>
);

const Personal = () => {
  const dispatch = useDispatch();
  const featureFlags = useFeatureFlags() as { emailCompliance: boolean; };
  const user = useUser();
  const loading = usePersonalLoading();
  const validRoutingNumberOptions = useValidRoutingNumberOptions();
  const [routingNumber, setRoutingNumber] = useState<Option>(
    validRoutingNumberOptions.find((o) => String(o.value) === String(user.routingNumber?.id)) || null,
  );
  const [syncSetting, setSyncSetting] = useState<string>(
    SYNC_SETTING_OPTIONS.find((o) => o.value === user.gmailSyncSettingAttributes?.syncSetting)?.value ||
      SYNC_SETTING_OPTIONS[0].value,
  );
  const gmailUnsync = useGmailUnsync();
  const googleProfile = useGoogleProfile();
  const googleAuthUrl = useGoogleAuthUrl();
  const emailPlaceholders = useEmailPlaceholders();
  const errors = usePersonalErrors();

  return (
    <div id="account-content" className="tw-max-w-[1024px] tw-mx-auto">
      <form
        className="tw-flex tw-flex-col tw-gap-[24px]"
        onSubmit={(e) => {
          e.preventDefault();
          const formData = Array.from(e.currentTarget.elements).reduce((acc, element) => {
            if (!(
              element instanceof HTMLInputElement ||
              element instanceof HTMLSelectElement ||
              element instanceof HTMLTextAreaElement
            )) {
              return acc;
            }

            if (element.type === "radio" && !(element as HTMLInputElement).checked) return acc;

            const key = element.name;
            const value =
              element.type === "checkbox" ? (element as HTMLInputElement).checked : element.value?.trim();
            if (key && key !== "image") return set(acc, key, value);
            return acc;
          }, {}) as User;
          // @ts-expect-error dispatch
          dispatch(updatePersonalAccount(formData));
        }}
      >
        {gmailUnsync && (
          <Alert
            variant="error"
            title="Gmail Unsync"
            text="Looks like your Gmail has become unsynced. Emails may not send correctly. Please click “Sign-in with Google” below to resync."
          />
        )}

        <Header>Login Credentials</Header>

        <div className="tw-flex tw-flex-wrap tw-flex-col tw-gap-[12px]">
          {/* @ts-expect-error props */}
          <Typography>Update the email address or password you use to login to Brivity.</Typography>
          <div className="tw-flex tw-items-start tw-gap-[16px]">
            <TextInput
              // @ts-expect-error props
              containerClassName="tw-flex-1"
              label="Email Address (For Log-In)"
              name="email"
              type="email"
              error={errors?.email?.[0]}
              defaultValue={user.email}
              autoComplete="off"
            />
            <div className="tw-flex-1 tw-pt-[2px]">
              <OktaPasswordReset />
            </div>
          </div>
        </div>

        {/* @ts-expect-error props */}
        <Header action={<TextLink href={`/people/${user.slug}`}>My Profile</TextLink>}>Personal Info</Header>

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-[16px]">
          <input type="hidden" name="personAttributes.id" value={user.personAttributes.id} />
          <TextInput
            // @ts-expect-error props
            containerClassName="tw-flex-1"
            name="personAttributes.firstName"
            error={errors?.["person.first_name"]?.[0]}
            label="First Name"
            autoComplete="off"
            defaultValue={user.personAttributes.firstName}
          />
          <TextInput
            // @ts-expect-error props
            containerClassName="tw-flex-1"
            name="personAttributes.lastName"
            label="Last Name"
            error={errors?.["person.last_name"]?.[0]}
            autoComplete="off"
            defaultValue={user.personAttributes.lastName}
          />
        </div>

        <div className="tw-flex tw-flex-wrap tw-gap-[16px]">
          <Dropdown
            // @ts-expect-error props
            containerClassName="tw-flex-1"
            options={validRoutingNumberOptions}
            label="Routing Number (Your Actual Phone Number)"
            name="routingNumberId"
            value={routingNumber}
            onChange={(o?: Option) => {
              if (o?.value) setRoutingNumber(o);
              else setRoutingNumber(null);
            }}
          />
          {user.managedNumber && (
            <TextInput
              // @ts-expect-error props
              containerClassName="tw-flex-1"
              label="Brivity Number"
              value={user.managedNumber}
              readOnly
            />
          )}
        </div>

        <div className="tw-flex tw-flex-wrap tw-gap-[16px]">
          <input
            type="hidden"
            name="personAttributes.emailDetailsAttributes[0].id"
            value={user.personAttributes.emailDetailsAttributes[0].id}
          />
          <input
            type="hidden"
            name="personAttributes.emailDetailsAttributes[0].category"
            value={user.personAttributes.emailDetailsAttributes[0].category}
          />
          <TextInput
            // @ts-expect-error props
            containerClassName="tw-flex-1"
            label="Primary Email Address"
            name="personAttributes.emailDetailsAttributes[0].value"
            defaultValue={user.personAttributes.emailDetailsAttributes[0].value}
            error={errors?.["person.base"]?.[0] || errors?.["person_email_details.value"]?.[0]}
            autoComplete="off"
          />
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[4px]">
            {/* @ts-expect-error props */}
            <FieldLabel label="Connect your email for two-way sync" />
            {!googleProfile.sub && (
              <div className="tw-flex tw-flex-col tw-gap-[8px]">
                <a href={googleAuthUrl} id="google-auth-link">
                  <img className="tw-max-w-[200px]" src="/images/google_sign_in_button.png" />
                </a>
                {/* @ts-expect-error props */}
                <Typography>
                  To prevent inbox sync issues, we recommend using the same email for your log in and
                  connected email.
                </Typography>
                {/* @ts-expect-error props */}
                <Typography>
                  Need help troubleshooting?{" "}
                  <a
                    href="https://www.brivityknowledge.com/docs/connect-gmail-to-brivity-crm?highlight=gmail"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Start here.
                  </a>
                </Typography>
              </div>
            )}
            {googleProfile.sub && (
              <div
                id="gmail-connected-card"
                className="tw-flex tw-flex-col tw-gap-[16px] tw-py-[16px] tw-bg-white tw-border-solid tw-border-[1px] tw-border-neutral-gray-10 tw-rounded-[12px]"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-[16px] tw-px-[16px]">
                  <div className="tw-flex tw-items-center tw-gap-[6px] tw-min-w-0">
                    <img
                      src="/images/google-g.png"
                      alt="Google"
                      className="tw-w-[24px] tw-h-[24px] tw-flex-none"
                    />
                    <div className="tw-flex tw-flex-col tw-min-w-0">
                      {/* @ts-expect-error props */}
                      <Typography variant="subtitle-s" className="tw-break-words">
                        {googleProfile.email}
                      </Typography>
                      <div className="tw-flex tw-items-center tw-gap-[6px]">
                        <CircleSmallSolidV6 size="m" className="tw-text-semantic-green-100" />
                        {/* @ts-expect-error props */}
                        <Typography variant="body-s" className="!tw-text-neutral-gray-50">
                          Connected
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <DisconnectGmailModal />
                </div>
                <hr className="tw-m-0 tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-neutral-gray-10" />
                <div className="tw-flex tw-flex-col tw-gap-[4px] tw-px-[16px]">
                  {/* No id is posted: the setting is update_only, so Rails updates the user's
                      own row or builds one, and a submitted id is ignored either way. */}
                  {/* @ts-expect-error props */}
                  <FieldLabel label="Email visibility" />
                  <div className="tw-flex tw-flex-col tw-gap-[12px]">
                    {SYNC_SETTING_OPTIONS.map(({ label, value, Icon, helperText }) => (
                      <div key={value} className="tw-flex tw-flex-col tw-gap-[6px]">
                        <div className="tw-flex tw-items-center tw-gap-[8px]">
                          {/* @ts-expect-error props */}
                          <RadioButton
                            label={label}
                            value={value}
                            name="gmailSyncSettingAttributes.syncSetting"
                            checked={syncSetting === value}
                            onChange={() => setSyncSetting(value)}
                          />
                          <Icon size="m" className="tw-text-neutral-gray-75" />
                        </div>
                        {/* @ts-expect-error props */}
                        <Typography>{helperText}</Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <div className="tw-flex tw-gap-[16px]">
            {/* @ts-expect-error props */}
            <FieldLabel className="tw-flex-1" label="MLS" />
            {/* @ts-expect-error props */}
            <FieldLabel className="tw-flex-1" label="MLS Agent ID" />
            <div className="tw-w-[32px] tw-flex-none" />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-[24px]">
            {user.usersMlsesAttributes?.map((mls, i) => (
              <MlsRow key={mls.id || mls.tmpId} mls={mls} i={i} />
            ))}
          </div>
        </div>

        {/* @ts-expect-error props */}
        <TextButton
          className="tw-self-start"
          onClick={() => {
            dispatch(addMls());
          }}
        >
          + MLS
        </TextButton>

        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          {user.signatureAttributes?.id && (
            <input type="hidden" name="signatureAttributes.id" value={user.signatureAttributes?.id} />
          )}
          {/* @ts-expect-error props */}
          <FieldLabel label="Email Signature" />
          {/* @ts-expect-error props */}
          <Typography className="!tw-text-gray-50">
            Note: Images in your email signature cannot be larger than 5MB. {/* @ts-expect-error props */}
            <TextLink
              href="https://www.brivityknowledge.com/docs/how-to-upload-images-to-your-email-signature"
              target="_blank"
              rel="noreferrer"
            >
              Learn more about how to upload images.
            </TextLink>
          </Typography>
          {/* @ts-expect-error props */}
          <BrivityTextEditor
            id="email-signature"
            name="signatureAttributes.content"
            placeholders={emailPlaceholders}
            settings={{
              paste_data_images: false,
              plugins: "lists advlist link emoticons placeholder preview image media code",
              file_picker_callback: (cb, value, meta) => {
                if ((meta as { filetype: string }).filetype === "image") {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.addEventListener("change", (e) => {
                    const file = (e.target as HTMLInputElement).files[0];
                    if (file.size > 1024 * 1024 * 5) {
                      document.dispatchEvent(new Event("account:imageSizeError"));
                    } else {
                      const reader = new FileReader();
                      reader.addEventListener("load", () => {
                        (cb as (arg: string | ArrayBuffer | null) => void)(reader.result);
                      });
                      reader.readAsDataURL(file);
                    }
                  });
                  input.click();
                }
              },
            }}
            defaultValue={user.signatureAttributes?.content}
          />
        </div>

        {featureFlags.emailCompliance && (
          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <FieldLabel
              // @ts-expect-error props
              label={
                <div className="tw-flex tw-items-center tw-gap-[4px]">
                  Email Compliance{" "}
                  <Tooltip
                    // @ts-expect-error props
                    className="tw-flex"
                    innerClassName="tw-max-w-[250px]"
                    trigger={<CircleInfoSolidV6 size="l" className="tw-text-theme-primary" />}
                    content="Use this field to add compliance related text to the footer of your Brivity emails, including Listing Alerts"
                  />
                </div>
              }
            />
            {/* @ts-expect-error props */}
            <BrivityTextEditor
              id="email-compliance"
              name="signatureAttributes.compliance"
              simple
              defaultValue={user.signatureAttributes?.compliance}
            />
          </div>
        )}

        <Header>Notification Settings</Header>

        <div>
          {/* @ts-expect-error props */}
          <Typography variant="subtitle-m-bold">Lead Notifications</Typography>
          {/* @ts-expect-error props */}
          <Typography className="!tw-text-gray-50">
            Choose how would you like to be notified when you get a lead.
          </Typography>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          {/* @ts-expect-error props */}
          <Checkbox
            defaultChecked={user.newLeadEmailNotifications}
            label="Send an email to my Primary Email"
            name="newLeadEmailNotifications"
          />
          {/* @ts-expect-error props */}
          <Checkbox
            defaultChecked={user.textNotifications}
            label="Send a text to my Text Routing Number (notifications will come from (360) 526-8915)"
            name="textNotifications"
          />
          {/* @ts-expect-error props */}
          <Checkbox
            defaultChecked={user.createLeadsFromUnrecognizedIncoming}
            label="Create phone number only leads for inbound calls to my Brivity number that are not in my CRM. This does not apply to KWKLY leads."
            name="createLeadsFromUnrecognizedIncoming"
          />
        </div>

        <div>
          {/* @ts-expect-error props */}
          <Typography variant="subtitle-m-bold">Text Forwarding</Typography>
          {/* @ts-expect-error props */}
          <Typography className="!tw-text-gray-50">
            Send a text to your routing number when a contact texts your Brivity number.
          </Typography>
        </div>

        <EnableTextForwarding textForwardingEnabled={user.smsTextForwarding} />

        <div className="tw-flex tw-justify-end">
          {/* @ts-expect-error props */}
          <Button size="medium" type="submit" isLoading={loading}>
            Save Changes
          </Button>
        </div>
      </form>

      <ImageSizeErrorModal />
      <GmailPermissionModal />
    </div>
  );
};

export default Personal;
